import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import { Spinner } from '@uikit';

import { useAuth } from '@shared/common/providers/AuthProvider';
import truncate from '@shared/helpers/web3/truncate';

import { useAutoLayerParticipantListInfiniteQuery, useSelfAutoLayerParticipant } from '@query/story';

import { formatPoints } from './AutoLayerStoryLeaderboardTable';

const AutoLayerStoryLeaderboardList = ({ className }: { className?: string }) => {
  const { isAuth } = useAuth();
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useAutoLayerParticipantListInfiniteQuery();
  const { data: selfParticipant } = useSelfAutoLayerParticipant({
    enabled: isAuth,
  });

  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const allParticipants = useMemo(() => {
    const participants = data?.pages.flatMap((page) => page) || [];

    if (!selfParticipant?.id) {
      return participants;
    }

    const hasSelfParticipant = !!participants.find(({ id }) => id === selfParticipant.id);

    if (!hasSelfParticipant) {
      participants.push(selfParticipant);
    }

    return participants;
  }, [data, selfParticipant]);

  const showLoader = isLoading || hasNextPage;
  const showEmpty = !isLoading && !allParticipants?.length;
  const showContent = !!allParticipants.length;

  return (
    <div className={classNames('overflow-y-auto flex flex-col max-h-[25rem] min-h-60', className)}>
      {showContent && (
        <ul className="flex flex-col gap-2">
          {allParticipants.map((participant) => {
            const points = [
              {
                name: 'Zealy',
                value: participant.attributes.soQuestPoints,
              },
              {
                name: 'AutoLayer',
                value: participant.attributes.autoLayerPoints,
              },
              {
                name: 'Total',
                value: participant.attributes.totalPoints,
              },
            ];

            const isSelfParticipant = selfParticipant && selfParticipant?.id === participant.id;

            return (
              <li
                key={participant.id}
                className={classNames(
                  'rounded-lg flex flex-col gap-2 bg p-2 border bg-neutral-50 border-primary-200',
                  {
                    'sticky top-0 lg:top-10 bg-primary-200 -order-1': isSelfParticipant,
                  },
                )}
              >
                <p className="flex items-center justify-between">
                  <span className="text-neutral-500 text-sm font-light">
                    {truncate(participant.id)}
                  </span>

                  <span>#{participant.attributes.position}</span>
                </p>

                <p className="flex gap-1 items-center text-xs text-primary-400">
                  <span className="h-0.5 bg-primary-100 w-full" />
                  Points
                  <span className="h-0.5 bg-primary-100 w-full" />
                </p>

                <ul className="flex flex-wrap gap-2">
                  {points.map(({ name, value }, i) => {
                    const isLastItem = i === points.length - 1;

                    return (
                      <li
                        key={name}
                        className={classNames(
                          'rounded-full flex items-center justify-center text-center bg-primary-100 p-1',
                          {
                            'w-full': isLastItem,
                            'flex-1': !isLastItem,
                          },
                        )}
                      >
                        {name} {formatPoints(value)}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}

      {showEmpty && (
        <p className="m-auto text-center text-neutral-200 text-sm">Leaderboard is Empty</p>
      )}

      {showLoader && (
        // Key is added to trigger in view cb again if component is already in view
        <div ref={ref} key={data?.pages.length} className="m-auto py-3">
          <Spinner size="md" />
        </div>
      )}
    </div>
  );
};

export default AutoLayerStoryLeaderboardList;
